import { useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api/client';

/**
 * Sales Summary — the dashboard's revenue figures for a range, with the lines
 * that add up to them.
 *
 * Built on `analytics/v2/reports/summary` rather than the legacy
 * `pos/sale/report`, which checked no role or store, added refunds back into
 * Gross Sales a second time, took no tax off orders the terminal never logged,
 * and put the gift card tender inside Collected. The new endpoint computes its
 * headline figures with the same code as the dashboard's `trends/sales`, so with
 * the same stores, dates and channel they are the dashboard's figures. See
 * `docs/api-findings-sales.md`.
 */

/** `TrendsOrderType`, in the order the page offers it. */
export const CHANNELS = [
  { value: 0, key: 'all' },
  { value: 2, key: 'pos' },
  { value: 3, key: 'selfCheckout' },
  { value: 1, key: 'online' },
] as const;

export type SummaryChannel = (typeof CHANNELS)[number]['value'];

/** What the dashboard's Revenue section always asks for. */
export const ALL_CHANNELS: SummaryChannel = 0;

/** `PosSalesReportTenderTypeSummary`. */
export interface SummaryTender {
  name: string | null;
  orders: number;
  /** Collected less refunds. */
  salesTotal: number;
  refundTotal: number;
  amountCollected: number;
}

/** `PosSalesReportTaxSummary`. */
export interface SummaryTax {
  taxName: string | null;
  applicableSales: number;
  amountCollected: number;
  amountRefunded: number;
  netAmount: number;
}

/** `SalesSummaryDropDto`. `createdAt` is UTC without a zone marker. */
export interface SummaryDrop {
  createdAt: string;
  amount: number;
  employee: string | null;
  laneTag: string | null;
  businessName: string | null;
}

/**
 * `SalesSummaryReportDto`. Two identities hold exactly:
 *
 *   grossSales − discounts + tax             = amountCollected
 *   grossSales − discounts − refundsBeforeTax = netSales
 */
export interface SalesSummary {
  orders: number;
  averageTicket: number;
  grossSales: number;
  discounts: number;
  terminalTax: number;
  unloggedTax: number;
  tax: number;
  amountCollected: number;
  refunds: number;
  refundTax: number;
  refundsBeforeTax: number;
  netSales: number;
  giftCardRedemptions: number;
  giftCardsSold: number;
  tenderTypes: SummaryTender[];
  cardTypes: SummaryTender[];
  taxes: SummaryTax[];
  drops: SummaryDrop[];
  dropsAmount: number;
  dropCount: number;
}

export interface SalesSummaryQuery {
  /** `yyyy-MM-dd`, a day at the store. */
  start: string;
  /** `yyyy-MM-dd`, included. */
  end: string;
  businessId: string | null;
  channel: SummaryChannel;
}

/**
 * Both picked days go out raw and both are included — the endpoint adds the
 * day itself. The dashboard's `trends/*` add it on the client instead; same
 * range either way.
 */
export function useSalesSummary(query: SalesSummaryQuery) {
  return useQuery({
    queryKey: ['sales-summary', query],
    staleTime: 60_000,
    queryFn: () =>
      post<SalesSummary>('analytics/v2/reports/summary', {
        startDate: query.start,
        endDate: query.end,
        businessIds: query.businessId ? [query.businessId] : null,
        type: query.channel,
      }),
    select: (summary) => ({
      ...summary,
      tenderTypes: summary.tenderTypes ?? [],
      cardTypes: summary.cardTypes ?? [],
      taxes: summary.taxes ?? [],
      drops: summary.drops ?? [],
    }),
  });
}
